import React from "react";
import { useState, useEffect } from "react";
import { TAKEOFF_PALETTE } from "../lib/constants.js";
import { KaininkasPositionSelect } from "./KaininkasPositionSelect.jsx";

export function TakeoffTemplateEditor({ tmpl, onSave, onCancel, allZinItems, existingGroups, ziniarastis, onNewGroup }) {
  const { useState, useEffect } = React;
  const [t, setT] = useState(() => tmpl ? { ...tmpl } : {
    id: Date.now(), name: '', shape: 'line', color: '#735DFF',
    strokeWidth: 2, size: 10, positions: [], groupName: ''
  });

  // Sinchronizuoti su tmpl prop pokyčiais (kai šablonas atnaujinamas iš išorės)
  useEffect(() => {
    if (tmpl) setT({ ...tmpl });
  }, [tmpl?.id, JSON.stringify(tmpl?.positions), tmpl?.shape, tmpl?.strokeWidth]);

  const [newPos, setNewPos] = useState({ itemId: '', name: '', unit: 'vnt.', qtyPerUnit: 1, price: 0 });
  const [selItem, setSelItem] = useState('');
  const [editingPosId, setEditingPosId] = useState(null);
  const editingPosIdRef = React.useRef(null);
  const [customGroup, setCustomGroup] = useState('');
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newlyAdded, setNewlyAdded] = useState([]);
  const allExisting = existingGroups || [];
  const localGroups = [...allExisting, ...newlyAdded.filter(g => !allExisting.includes(g))];

  // Sinchronizuoti ref su state
  useEffect(() => { editingPosIdRef.current = editingPosId; }, [editingPosId]);

  // Klaviatūros valdymas — document capture fazė, veikia prieš visus kitus
  useEffect(() => {
    function onKey(e) {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (editingPosIdRef.current) {
        setEditingPosId(null);
        setNewPos({ itemId: '', name: '', unit: 'vnt.', qtyPerUnit: 1, price: 0 });
        setSelItem('');
      } else {
        onCancel();
      }
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []); // tuščias dep array — handler registruojamas vieną kartą, naudoja ref

  function confirmNewGroup() {
    const n = customGroup.trim();
    if (!n) return;
    setT(p => ({...p, groupName: n}));
    setShowNewGroup(false);
    setCustomGroup('');
    // Saugome lokaliai kad iš karto matytume mygtuką
    setNewlyAdded(prev => prev.includes(n) ? prev : [...prev, n]);
    // Išsaugoti į projektą
    if (onNewGroup) onNewGroup(n);
  }

  function addPosition() {
    if (!newPos.itemId && !newPos.name.trim()) return;
    if (editingPosId) {
      // Redagavimo režimas — atnaujinti esamą
      setT(prev => ({ ...prev, positions: prev.positions.map(p => p.id === editingPosId ? { ...p, ...newPos } : p) }));
      setEditingPosId(null);
    } else {
      // Naujos pozicijos pridėjimas
      setT(prev => ({ ...prev, positions: [...(prev.positions||[]), { ...newPos, id: Date.now() }] }));
    }
    setNewPos({ itemId: '', name: '', unit: 'vnt.', qtyPerUnit: 1, price: 0 });
    setSelItem('');
  }
  function startEditPos(pos) {
    setEditingPosId(pos.id);
    setNewPos({ itemId: pos.itemId||'', name: pos.name||'', unit: pos.unit||'vnt.', qtyPerUnit: pos.qtyPerUnit||1, price: pos.price||0, groupName: pos.groupName||'', groupId: pos.groupId||'' });
  }
  function cancelEditPos() {
    setEditingPosId(null);
    setNewPos({ itemId: '', name: '', unit: 'vnt.', qtyPerUnit: 1, price: 0 });
    setSelItem('');
  }
  function removePos(id) {
    setT(prev => ({ ...prev, positions: prev.positions.filter(p=>p.id!==id) }));
    if (editingPosId === id) cancelEditPos();
  }

  const shapePreview = (() => {
    const col = t.color||'#735DFF'; const sz = t.size||10; const sw = t.strokeWidth||2;
    const cx=30,cy=15;
    // Žvaigždė 5 smaigalių
    const star5pts = (r1,r2,n,cx,cy) => Array.from({length:n*2},(_,i)=>{const a=Math.PI/n*i-Math.PI/2;const r=i%2===0?r1:r2;return `${cx+Math.cos(a)*r},${cy+Math.sin(a)*r}`;}).join(' ');
    // Žvaigždė 6 smaigalių
    const star6pts = (r1,r2,cx,cy) => Array.from({length:12},(_,i)=>{const a=Math.PI/6*i-Math.PI/2;const r=i%2===0?r1:r2;return `${cx+Math.cos(a)*r},${cy+Math.sin(a)*r}`;}).join(' ');
    // Daugiakampis
    const polyPts = (n,r,cx,cy,off=0) => Array.from({length:n},(_,i)=>{const a=2*Math.PI/n*i+off-Math.PI/2;return `${cx+Math.cos(a)*r},${cy+Math.sin(a)*r}`;}).join(' ');
    return {
      line: <svg width={60} height={30}><line x1={5} y1={15} x2={55} y2={15} stroke={col} strokeWidth={sw} strokeLinecap="round"/></svg>,
      polyline: <svg width={60} height={30}><polyline points="5,25 20,10 35,22 55,8" fill="none" stroke={col} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"/></svg>,
      circle: <svg width={60} height={30}><circle cx={cx} cy={cy} r={Math.min(sz,12)} fill={col}/></svg>,
      rect: <svg width={60} height={30}><rect x={cx-Math.min(sz,12)/2} y={cy-Math.min(sz,12)/2} width={Math.min(sz,12)} height={Math.min(sz,12)} fill={col}/></svg>,
      triangle: <svg width={60} height={30}><polygon points={`${cx},${cy-Math.min(sz,12)} ${cx-Math.min(sz,12)*0.866},${cy+Math.min(sz,12)*0.5} ${cx+Math.min(sz,12)*0.866},${cy+Math.min(sz,12)*0.5}`} fill={col}/></svg>,
      triangle_down: <svg width={60} height={30}><polygon points={`${cx},${cy+Math.min(sz,12)} ${cx-Math.min(sz,12)*0.866},${cy-Math.min(sz,12)*0.5} ${cx+Math.min(sz,12)*0.866},${cy-Math.min(sz,12)*0.5}`} fill={col}/></svg>,
      diamond: <svg width={60} height={30}><polygon points={`${cx},${cy-Math.min(sz,12)} ${cx+Math.min(sz,12)*0.75},${cy} ${cx},${cy+Math.min(sz,12)} ${cx-Math.min(sz,12)*0.75},${cy}`} fill={col}/></svg>,
      star: <svg width={60} height={30}><polygon points={star5pts(Math.min(sz,12),Math.min(sz,12)*0.45,5,cx,cy)} fill={col}/></svg>,
      star6: <svg width={60} height={30}><polygon points={star6pts(Math.min(sz,12),Math.min(sz,12)*0.5,cx,cy)} fill={col}/></svg>,
      cross: <svg width={60} height={30}><rect x={cx-Math.min(sz,12)/4} y={cy-Math.min(sz,12)} width={Math.min(sz,12)/2} height={Math.min(sz,12)*2} fill={col}/><rect x={cx-Math.min(sz,12)} y={cy-Math.min(sz,12)/4} width={Math.min(sz,12)*2} height={Math.min(sz,12)/2} fill={col}/></svg>,
      pentagon: <svg width={60} height={30}><polygon points={polyPts(5,Math.min(sz,12),cx,cy)} fill={col}/></svg>,
      hexagon: <svg width={60} height={30}><polygon points={polyPts(6,Math.min(sz,12),cx,cy)} fill={col}/></svg>,
      octagon: <svg width={60} height={30}><polygon points={polyPts(8,Math.min(sz,12),cx,cy)} fill={col}/></svg>,
      arrow_right: <svg width={60} height={30}><polygon points={`${cx-Math.min(sz,12)},${cy-Math.min(sz,12)*0.35} ${cx+Math.min(sz,12)*0.3},${cy-Math.min(sz,12)*0.35} ${cx+Math.min(sz,12)*0.3},${cy-Math.min(sz,12)*0.7} ${cx+Math.min(sz,12)},${cy} ${cx+Math.min(sz,12)*0.3},${cy+Math.min(sz,12)*0.7} ${cx+Math.min(sz,12)*0.3},${cy+Math.min(sz,12)*0.35} ${cx-Math.min(sz,12)},${cy+Math.min(sz,12)*0.35}`} fill={col}/></svg>,
      arrow_left: <svg width={60} height={30}><polygon points={`${cx+Math.min(sz,12)},${cy-Math.min(sz,12)*0.35} ${cx-Math.min(sz,12)*0.3},${cy-Math.min(sz,12)*0.35} ${cx-Math.min(sz,12)*0.3},${cy-Math.min(sz,12)*0.7} ${cx-Math.min(sz,12)},${cy} ${cx-Math.min(sz,12)*0.3},${cy+Math.min(sz,12)*0.7} ${cx-Math.min(sz,12)*0.3},${cy+Math.min(sz,12)*0.35} ${cx+Math.min(sz,12)},${cy+Math.min(sz,12)*0.35}`} fill={col}/></svg>,
      arrow_both: <svg width={60} height={30}><polygon points={`${cx-Math.min(sz,12)},${cy} ${cx-Math.min(sz,12)*0.4},${cy-Math.min(sz,12)*0.6} ${cx-Math.min(sz,12)*0.4},${cy-Math.min(sz,12)*0.3} ${cx+Math.min(sz,12)*0.4},${cy-Math.min(sz,12)*0.3} ${cx+Math.min(sz,12)*0.4},${cy-Math.min(sz,12)*0.6} ${cx+Math.min(sz,12)},${cy} ${cx+Math.min(sz,12)*0.4},${cy+Math.min(sz,12)*0.6} ${cx+Math.min(sz,12)*0.4},${cy+Math.min(sz,12)*0.3} ${cx-Math.min(sz,12)*0.4},${cy+Math.min(sz,12)*0.3} ${cx-Math.min(sz,12)*0.4},${cy+Math.min(sz,12)*0.6}`} fill={col}/></svg>,
      circle_cross: <svg width={60} height={30}><circle cx={cx} cy={cy} r={Math.min(sz,12)} fill="none" stroke={col} strokeWidth={sw}/><line x1={cx-Math.min(sz,12)} y1={cy} x2={cx+Math.min(sz,12)} y2={cy} stroke={col} strokeWidth={sw}/><line x1={cx} y1={cy-Math.min(sz,12)} x2={cx} y2={cy+Math.min(sz,12)} stroke={col} strokeWidth={sw}/></svg>,
      rect_cross: <svg width={60} height={30}><rect x={cx-Math.min(sz,12)} y={cy-Math.min(sz,12)} width={Math.min(sz,12)*2} height={Math.min(sz,12)*2} fill="none" stroke={col} strokeWidth={sw}/><line x1={cx-Math.min(sz,12)} y1={cy-Math.min(sz,12)} x2={cx+Math.min(sz,12)} y2={cy+Math.min(sz,12)} stroke={col} strokeWidth={sw}/><line x1={cx+Math.min(sz,12)} y1={cy-Math.min(sz,12)} x2={cx-Math.min(sz,12)} y2={cy+Math.min(sz,12)} stroke={col} strokeWidth={sw}/></svg>,
      double_circle: <svg width={60} height={30}><circle cx={cx} cy={cy} r={Math.min(sz,12)} fill="none" stroke={col} strokeWidth={sw}/><circle cx={cx} cy={cy} r={Math.min(sz,12)*0.55} fill={col}/></svg>,
      ellipse: <svg width={60} height={30}><ellipse cx={cx} cy={cy} rx={Math.min(sz,12)*1.5} ry={Math.min(sz,12)*0.7} fill={col}/></svg>,
    };
  })();

  return (
    <div style={{background:'#fff',borderRadius:12,padding:20,width:500,maxWidth:'96vw',boxShadow:'0 16px 48px rgba(0,0,0,0.18)',maxHeight:'90vh',overflowY:'auto'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <span style={{fontSize:14,fontWeight:800}}>{tmpl ? '✏️ Redaguoti šabloną' : '➕ Naujas šablonas'}</span>
        <button onClick={onCancel} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'#9ca3af'}}>×</button>
      </div>

      {/* Pavadinimas */}
      <div style={{marginBottom:10}}>
        <label style={{fontSize:11,fontWeight:600,color:'#6b7280',display:'block',marginBottom:3}}>Pavadinimas *</label>
        <input style={{width:'100%',border:'1px solid #e5e7eb',borderRadius:5,padding:'6px 8px',fontSize:13,boxSizing:'border-box'}}
          value={t.name} onChange={e=>setT(p=>({...p,name:e.target.value}))} placeholder="pvz. Vamzdis DN100" />
      </div>

      {/* Grupė */}
      <div style={{marginBottom:10}}>
        <label style={{fontSize:11,fontWeight:600,color:'#6b7280',display:'block',marginBottom:3}}>Grupė</label>
        <div style={{display:'flex',gap:5}}>
          <select
            value={t.groupName||''}
            onChange={e=>setT(p=>({...p,groupName:e.target.value}))}
            style={{flex:1,border:'1px solid #e5e7eb',borderRadius:5,padding:'6px 8px',fontSize:12,boxSizing:'border-box',color:'#374151',background:'#fff'}}>
            <option value="">— Nepriskirta —</option>
            {localGroups.map(g=><option key={g} value={g}>{g}</option>)}
          </select>
          <button onClick={()=>{setShowNewGroup(v=>!v);setCustomGroup('');}}
            title="Nauja grupė"
            style={{padding:'6px 10px',fontSize:11,borderRadius:5,border:'1.5px dashed #d1d5db',
              background:showNewGroup?'#f0f4f8':'transparent',color:'#9ca3af',cursor:'pointer',whiteSpace:'nowrap'}}>
            + nauja
          </button>
        </div>
        {showNewGroup && (
          <div style={{display:'flex',gap:5,marginTop:5}}>
            <input autoFocus style={{flex:1,border:'1px solid #e5e7eb',borderRadius:5,padding:'5px 8px',fontSize:12,boxSizing:'border-box'}}
              value={customGroup} onChange={e=>setCustomGroup(e.target.value)}
              onKeyDown={e=>{if(e.key==='Enter') confirmNewGroup();
                if(e.key==='Escape'){setShowNewGroup(false);setCustomGroup('');}}}
              placeholder="Grupės pavadinimas..." />
            <button onClick={confirmNewGroup}
              style={{background:'#383853',color:'#fff',border:'none',borderRadius:5,padding:'5px 10px',fontSize:11,fontWeight:700,cursor:'pointer'}}>OK</button>
          </div>
        )}
      </div>

      {/* Forma */}
      <div style={{marginBottom:10}}>
        <label style={{fontSize:11,fontWeight:600,color:'#6b7280',display:'block',marginBottom:4}}>Forma</label>
        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          {[['line','╱ Linija'],['polyline','〜 Polilinija'],['circle','● Apskritimas'],['rect','■ Stačiakampis'],['triangle','▲ Trikampis ↑'],['triangle_down','▼ Trikampis ↓'],['diamond','◆ Rombas'],['star','★ Žvaigždė 5'],['star6','✶ Žvaigždė 6'],['cross','✚ Kryžius'],['pentagon','⬠ Penkiakampis'],['hexagon','⬡ Šešiakampis'],['octagon','⯃ Aštuonkampis'],['arrow_right','➤ Rodyklė →'],['arrow_left','◄ Rodyklė ←'],['arrow_both','↔ Dvipusė →←'],['circle_cross','⊕ Apskr.+Kryž.'],['rect_cross','⊞ Kvadr.+Kryž.'],['double_circle','◎ Dvigubas ◯'],['ellipse','⬭ Ovalas']].map(([sh,lb])=>(
            <button key={sh} onClick={()=>setT(p=>({...p,shape:sh}))}
              style={{padding:'4px 9px',fontSize:11,fontWeight:700,borderRadius:6,border:'2px solid '+(t.shape===sh?'#383853':'#e5e7eb'),background:t.shape===sh?'#383853':'#f9fafb',color:t.shape===sh?'#fff':'#374151',cursor:'pointer'}}>
              {lb}
            </button>
          ))}
        </div>
      </div>

      {/* Spalva ir dydis */}
      <div style={{display:'flex',gap:10,marginBottom:10,alignItems:'flex-end'}}>
        <div style={{flex:1}}>
          <label style={{fontSize:11,fontWeight:600,color:'#6b7280',display:'block',marginBottom:4}}>Spalva</label>
          <div style={{display:'flex',flexWrap:'wrap',gap:5,marginBottom:4}}>
            {TAKEOFF_PALETTE.map(c=>(
              <button key={c} onClick={()=>setT(p=>({...p,color:c}))}
                style={{width:20,height:20,borderRadius:'50%',border:t.color===c?'3px solid #1c1c1c':'2px solid #e5e7eb',background:c,cursor:'pointer'}} />
            ))}
          </div>
          <input type="color" value={t.color||'#735DFF'} onChange={e=>setT(p=>({...p,color:e.target.value}))}
            style={{width:32,height:26,padding:0,border:'1px solid #e5e7eb',borderRadius:4,cursor:'pointer'}}/>
        </div>
        <div style={{minWidth:110}}>
          <label style={{fontSize:11,fontWeight:600,color:'#6b7280',display:'block',marginBottom:3}}>
            {t.shape==='line'||t.shape==='polyline' ? 'Linijos storis' : 'Dydis (px)'}
          </label>
          <input type="number" min={1} max={40} value={t.shape==='line'||t.shape==='polyline'?t.strokeWidth||2:t.size||10}
            onChange={e=>{const v=parseInt(e.target.value)||2; setT(p=>t.shape==='line'||t.shape==='polyline'?{...p,strokeWidth:v}:{...p,size:v});}}
            style={{width:'100%',border:'1px solid #e5e7eb',borderRadius:5,padding:'5px 7px',fontSize:13,boxSizing:'border-box'}} />
        </div>
        <div style={{minWidth:60,display:'flex',flexDirection:'column',alignItems:'center'}}>
          <label style={{fontSize:11,fontWeight:600,color:'#6b7280',marginBottom:3}}>Peržiūra</label>
          {shapePreview[t.shape] || null}
        </div>
      </div>

      {/* Žiniaraščio pozicijos */}
      <div style={{marginBottom:10}}>
        <label style={{fontSize:12,fontWeight:700,color:'#374151',display:'block',marginBottom:6}}>📋 Žiniaraščio pozicijos</label>
        {(t.positions||[]).length === 0 && <div style={{fontSize:11,color:'#9ca3af',marginBottom:6}}>Nepriskirta jokiai pozicijai</div>}
        {(t.positions||[]).map(pos=>(
          <div key={pos.id} style={{display:'flex',alignItems:'center',gap:6,padding:'5px 8px',
            background: editingPosId===pos.id ? '#fffbeb' : '#f8f9fb',
            border:'1px solid '+(editingPosId===pos.id?'#f59e0b':'#e5e7eb'),
            borderRadius:6,marginBottom:4}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,fontWeight:600,color:'#1c1c1c'}}>{pos.name}</div>
              <div style={{fontSize:11,color:'#6b7280'}}>{pos.unit} · ×{pos.qtyPerUnit} {(t.shape==='line'||t.shape==='polyline')?'vnt/m':'vnt'}</div>
              {pos.groupName && <div style={{fontSize:10,color:'#9ca3af'}}>{pos.groupName}</div>}
            </div>
            <button onClick={()=>startEditPos(pos)}
              style={{background:'none',border:'none',color: editingPosId===pos.id?'#f59e0b':'#9ca3af',cursor:'pointer',fontSize:13,padding:'0 2px'}}>✏</button>
            <button onClick={()=>removePos(pos.id)}
              style={{background:'none',border:'none',color:'#ef4444',cursor:'pointer',fontSize:14,fontWeight:700,padding:'0 2px'}}>×</button>
          </div>
        ))}
        <div style={{background:'#f0f4f8',border:'1px dashed '+(editingPosId?'#f59e0b':'#d1d5db'),borderRadius:7,padding:'10px',marginTop:6}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
            <div style={{fontSize:11,fontWeight:700,color: editingPosId?'#92400e':'#374151'}}>
              {editingPosId ? '✏ Redaguoti poziciją' : '+ Pridėti poziciją'}
            </div>
            {editingPosId && (
              <button onClick={cancelEditPos}
                style={{background:'none',border:'none',fontSize:12,color:'#9ca3af',cursor:'pointer'}}>✕ Atšaukti</button>
            )}
          </div>
          {/* Kainininkas pasirinkimas: tiekėjas → gamintojas → medžiaga */}
          <KaininkasPositionSelect allKainininkai={ziniarastis||[]} onSelect={it=>{
            setSelItem(it.id);
            setNewPos(p=>({...p,itemId:it.id,name:it.name,unit:it.unit,price:parseFloat(it.priceEx)||0,groupName:it.groupName||'',groupId:it.groupId||''}));
          }} />
          <div style={{display:'flex',gap:6,marginBottom:6}}>
            <div style={{flex:2}}>
              <label style={{fontSize:10,fontWeight:600,color:'#6b7280',display:'block',marginBottom:2}}>Pozicijos pavadinimas</label>
              <input style={{width:'100%',border:'1px solid #e5e7eb',borderRadius:5,padding:'4px 7px',fontSize:12,boxSizing:'border-box'}}
                value={newPos.name} onChange={e=>setNewPos(p=>({...p,name:e.target.value}))} placeholder="pvz. Vamzdis DN100" />
            </div>
            <div style={{width:70}}>
              <label style={{fontSize:10,fontWeight:600,color:'#6b7280',display:'block',marginBottom:2}}>Mato vnt.</label>
              <input style={{width:'100%',border:'1px solid #e5e7eb',borderRadius:5,padding:'4px 7px',fontSize:12,boxSizing:'border-box'}}
                value={newPos.unit} onChange={e=>setNewPos(p=>({...p,unit:e.target.value}))} />
            </div>
            <div style={{width:80}}>
              <label style={{fontSize:10,fontWeight:600,color:'#6b7280',display:'block',marginBottom:2}}>
                {(t.shape==='line'||t.shape==='polyline')?'Kiekis/m':'Kiekis/vnt'}
              </label>
              <input type="number" step="0.01" min="0"
                style={{width:'100%',border:'1px solid #e5e7eb',borderRadius:5,padding:'4px 7px',fontSize:12,boxSizing:'border-box'}}
                value={newPos.qtyPerUnit} onChange={e=>setNewPos(p=>({...p,qtyPerUnit:parseFloat(e.target.value)||1}))} />
            </div>
          </div>
          <button onClick={addPosition} disabled={!newPos.name.trim()}
            style={{width:'100%',background:newPos.name.trim()?(editingPosId?'#f59e0b':'#383853'):'#e5e7eb',color:newPos.name.trim()?'#fff':'#9ca3af',border:'none',borderRadius:6,padding:'6px',fontSize:12,fontWeight:700,cursor:'pointer'}}>
            {editingPosId ? '✓ Išsaugoti pakeitimus' : '+ Pridėti poziciją'}
          </button>
        </div>
      </div>

      <div style={{display:'flex',gap:8,marginTop:14}}>
        <button onClick={()=>{ if(!t.name.trim()) return; onSave(t); }}
          disabled={!t.name.trim()}
          style={{flex:1,background:t.name.trim()?'#383853':'#e5e7eb',color:t.name.trim()?'#fff':'#9ca3af',border:'none',borderRadius:7,padding:'9px',fontSize:13,fontWeight:700,cursor:'pointer'}}>
          💾 Išsaugoti šabloną
        </button>
        <button onClick={onCancel} style={{background:'#f3f4f6',border:'none',borderRadius:7,padding:'9px 14px',fontSize:13,cursor:'pointer'}}>Atšaukti</button>
      </div>
    </div>
  );
}
